/**
 * XoeTurnActivity — the live agent-progress block (P3.3).
 *
 * Replaces `TypingIndicator`'s single "Thinking…" line with an ordered list of what the
 * agent is actually doing, driven by `tool.requested` / `tool.completed` frames.
 *
 * *** THE OLD BEHAVIOUR IS THE FLOOR, NEVER A REGRESSION. ***
 * With zero activities this renders exactly what `TypingIndicator` rendered — XoeMark +
 * a spinner + the literal "Thinking…" — because that is the state during the first seconds of
 * EVERY turn, and the whole of any turn that calls no tools. The activity list is
 * strictly additive on top of it.
 *
 * *** LABELS COME OFF THE WIRE VERBATIM. ***
 * `tool_labels.py::label_for()` owns the vocabulary and never echoes an unrecognised
 * argument. There is no client-side label table here, deliberately: a table would drift
 * from the registry, and a reworded progress message would need two repos and a
 * coordinated release instead of one server-side line. If the server and its registry
 * drift, a practitioner sees the constant "Working" — degraded, never misleading.
 *
 * *** THE ELAPSED TIMER'S INTERVAL LIVES HERE, AND THAT IS LOAD-BEARING. ***
 * Hoisting the 1s tick into `XoeChat` would re-render the entire message thread
 * every second for the whole of a multi-minute turn. Keeping it local is a performance
 * decision, not a stylistic one — do not "tidy" it upward.
 */

import React, { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import {
	LoadingOutlined,
	CheckCircleOutlined,
	StopOutlined,
	ExclamationCircleOutlined,
	BulbOutlined,
	RightOutlined,
	DownOutlined,
} from '@ant-design/icons';

import XoeMark from './XoeMark';
import type { XoeToolActivity } from '../../types/xoe/XoeStreamTypes';
import type { XoeTurnActivityProps, XoeReasoningRowProps } from '../../types/xoe/XoeUiTypes';

const { Text } = Typography;

// ── Elapsed timer ─────────────────────────────────────────────────────────────

/** Its own component so its 1s tick re-renders only this line. */
const ElapsedTimer: React.FC<{ startedAt: number }> = ({ startedAt }) => {
	const [seconds, setSeconds] = useState(() =>
		Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
	);

	useEffect(() => {
		const id = setInterval(() => {
			setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
		}, 1000);
		return () => clearInterval(id);
	}, [startedAt]);

	return (
		<Text
			type="secondary"
			data-testid="xoe-turn-elapsed"
			style={{ fontSize: 'var(--mg-fs-xs)' }}
		>
			{seconds}s
		</Text>
	);
};

// ── One activity row ──────────────────────────────────────────────────────────

/**
 * Trailing text per state.
 *
 * `denied` is NOT an error and never says so — see the module docstring and
 * `turn_stream.py::_tool_completed`, which carries `denied` as its own boolean for
 * exactly this reason.
 */
const stateSuffix = (state: XoeToolActivity['state']): string | null => {
	switch (state) {
		case 'denied':
			return 'Not permitted';
		case 'failed':
			return "Couldn't complete";
		default:
			return null;
	}
};

const stateIcon = (state: XoeToolActivity['state']): React.ReactNode => {
	switch (state) {
		case 'running':
			return <LoadingOutlined style={{ fontSize: 'var(--mg-fs-xs)' }} spin />;
		case 'done':
			return <CheckCircleOutlined style={{ fontSize: 'var(--mg-fs-xs)', color: 'var(--mg-success)' }} />;
		case 'denied':
			// Neutral, not red. A clinic configuration decision is not a fault.
			return <StopOutlined style={{ fontSize: 'var(--mg-fs-xs)', color: 'var(--mg-text-secondary)' }} />;
		case 'failed':
			return <ExclamationCircleOutlined style={{ fontSize: 'var(--mg-fs-xs)', color: 'var(--mg-warning)' }} />;
		default:
			return null;
	}
};

const ActivityRow: React.FC<{ activity: XoeToolActivity }> = ({ activity }) => {
	const suffix = stateSuffix(activity.state);

	return (
		<div
			data-testid="xoe-turn-activity-row"
			data-state={activity.state}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 'var(--mg-sp-3)',
				fontSize: 'var(--mg-fs-xs)',
				opacity: activity.state === 'running' ? 1 : 0.75,
			}}
		>
			<span style={{ display: 'inline-flex', width: 12, justifyContent: 'center' }}>
				{stateIcon(activity.state)}
			</span>
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
				{activity.label}
			</Text>
			{suffix && (
				<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', fontStyle: 'italic' }}>
					· {suffix}
				</Text>
			)}
		</div>
	);
};

// ── One reasoning row (P7.1) ──────────────────────────────────────────────────

/**
 * A collapsible reasoning entry.
 *
 * *** THERE IS NO EXISTING COLLAPSIBLE PATTERN IN THE XOE TREE — this establishes one.
 * *** Ant Design's `Collapse` is used elsewhere in the app (`PlanSectionAccordion`) but
 * is card-scale: its own borders, padding and 14px chrome would tower over the 11px
 * icons, `--mg-fs-xs` text and tight gap of the tool rows it sits between, and the whole
 * point of this row is that it reads as part of that timeline. A local `useState`
 * disclosure with a caret in the SAME 12px icon slot `ActivityRow` uses keeps the two
 * visually continuous.
 *
 * The text is already scrubbed server-side and is rendered as PLAIN TEXT, never
 * markdown: it is model output, and handing it to a markdown renderer would let it
 * emit links and images from a channel that exists to be read, not to act.
 */
export const ReasoningRow: React.FC<XoeReasoningRowProps> = ({
	entry,
	defaultExpanded = false,
}) => {
	const [expanded, setExpanded] = useState(defaultExpanded);

	return (
		<div
			data-testid="xoe-turn-reasoning-row"
			data-expanded={String(expanded)}
			style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mg-sp-1)' }}
		>
			<div
				role="button"
				tabIndex={0}
				className="mg-xoe-focus-ring"
				aria-expanded={expanded}
				data-testid="xoe-turn-reasoning-toggle"
				onClick={() => setExpanded((open) => !open)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setExpanded((open) => !open);
					}
				}}
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 'var(--mg-sp-3)',
					cursor: 'pointer',
					fontSize: 'var(--mg-fs-xs)',
				}}
			>
				{/* Same 12px slot the tool rows use, so the two lists stay aligned. */}
				<span style={{ display: 'inline-flex', width: 12, justifyContent: 'center' }}>
					<BulbOutlined style={{ fontSize: 'var(--mg-fs-xs)', color: 'var(--mg-text-secondary)' }} />
				</span>
				<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
					Reasoning
				</Text>
				{expanded ? (
					<DownOutlined style={{ fontSize: 'var(--mg-fs-xxs)', color: 'var(--mg-text-secondary)' }} />
				) : (
					<RightOutlined style={{ fontSize: 'var(--mg-fs-xxs)', color: 'var(--mg-text-secondary)' }} />
				)}
			</div>

			{expanded && (
				<Text
					type="secondary"
					data-testid="xoe-turn-reasoning-text"
					style={{
						fontSize: 'var(--mg-fs-xs)',
						fontStyle: 'italic',
						// Aligned under the label, clear of the icon slot.
						paddingLeft: 18,
						whiteSpace: 'pre-wrap',
						opacity: 0.85,
					}}
				>
					{entry.text}
				</Text>
			)}
		</div>
	);
};

// ── Block ─────────────────────────────────────────────────────────────────────

const XoeTurnActivity: React.FC<XoeTurnActivityProps> = ({ progress, onCancel }) => {
	const hasActivities = progress.entries.length > 0;

	return (
		<div
			data-testid="xoe-turn-activity"
			style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'var(--mg-sp-5)' }}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 'var(--mg-sp-3)',
					padding: 'var(--mg-sp-4) var(--mg-sp-5)',
					borderRadius:
						'var(--mg-radius-lg) var(--mg-radius-lg) var(--mg-radius-lg) var(--mg-radius-sm)',
					background: 'var(--mg-bg-elevated)',
					border: '1px solid var(--mg-border)',
					maxWidth: '90%',
				}}
			>
				{/* Header: mark + status + elapsed + cancel. Same chrome as the old indicator. */}
				<div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mg-sp-3)' }}>
					<span
						style={{ color: 'var(--mg-text-secondary)', opacity: 0.5, display: 'inline-flex' }}
					>
						<XoeMark size={14} animated={false} />
					</span>

					{!hasActivities && (
						<>
							<LoadingOutlined style={{ fontSize: 'var(--mg-fs-md)' }} spin />
							<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
								Thinking…
							</Text>
						</>
					)}

					{hasActivities && (
						<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)' }}>
							Working…
						</Text>
					)}

					<ElapsedTimer startedAt={progress.startedAt} />

					{onCancel && (
						<Button
							type="text"
							size="small"
							onClick={onCancel}
							data-testid="xoe-turn-cancel"
							aria-label="Cancel this turn"
							style={{
								height: 'auto',
								padding: '0 var(--mg-sp-2)',
								fontSize: 'var(--mg-fs-xs)',
								color: 'var(--mg-text-secondary)',
							}}
						>
							Cancel
						</Button>
					)}
				</div>

				{/*
				  * The live timeline — tool activity AND reasoning, IN ARRIVAL ORDER.
				  *
				  * Rendering one ordered array is what makes the interleaving true rather
				  * than approximated. Two lists rendered one after the other would show
				  * every thought above every tool call regardless of when the agent
				  * actually had them, which is a different and misleading story about how
				  * it worked.
				  *
				  * Reasoning auto-expands here because this list only exists WHILE THE TURN
				  * IS STREAMING — the practitioner is watching it think. The replay path in
				  * `XoeMessageBubble` renders the same row collapsed.
				  */}
				{hasActivities && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mg-sp-1)' }}>
						{progress.entries.map((entry) =>
							entry.kind === 'tool' ? (
								<ActivityRow
									key={`tool:${entry.activity.toolUseId}`}
									activity={entry.activity}
								/>
							) : (
								<ReasoningRow
									key={`reasoning:${entry.entry.index}`}
									entry={entry.entry}
									defaultExpanded
								/>
							),
						)}
					</div>
				)}

				{/* The turn hit its iteration cap: the answer is real, but may be partial. */}
				{progress.capped && (
					<Text
						type="secondary"
						data-testid="xoe-turn-capped"
						style={{ fontSize: 'var(--mg-fs-xs)', fontStyle: 'italic' }}
					>
						Answered with the information gathered so far.
					</Text>
				)}
			</div>
		</div>
	);
};

export default XoeTurnActivity;
